import * as React from 'react'
import dynamic from 'next/dynamic'

import {
  ElevationLevel,
  spacing,
  styled,
  Text,
  zIndex,
  ZIndexLevel,
} from '@thg-commerce/enterprise-theme'
import {
  isLiveChatAvailable,
  getLiveChatContainer,
} from '@thg-commerce/enterprise-utils/src/LiveChat/LiveChat'
const LiveChatOutline = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/LiveChatOutline'),
)
const LiveChat = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/LiveChat'),
)
const SvgIcon = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/SvgIcon'),
)

import { i18n, useTheme } from '../../../..'
import { Content, LeftContent } from '../styles'

const liveChatAvailabilityCheckInterval = 2000

const LiveChatContainer = styled.button<{ alternateText?: boolean }>`
  ${(props) => Text('bodyText', props.alternateText ? 'alternate' : 'default')}
  display: flex;
  align-items: center;
  position: relative;
`

const LiveChatIndicator = styled.span<{ isActive?: boolean }>`
  display: block;
  margin-left: ${spacing(1)};
  min-width: 8px;
  min-height: 8px;
  background-color: ${(props) =>
    props.isActive
      ? props.theme.colors.success.base
      : props.theme.colors.palette.greys.grey};
  border-radius: 50%;
`

const LiveChatUnavailableMessage = styled.div`
  display: block;
  position: absolute;
  top: 100%;
  left: 0;
  border-radius: 4px;

  &::before,
  &::after {
    ${zIndex(ZIndexLevel.Lower)};
    content: '';
    transform: rotate(45deg);
    width: 16px;
    height: 16px;
    position: absolute;
    top: -8px;
    left: 20%;
    background-color: ${(props) => props.theme.colors.palette.greys.white};
  }

  &::before {
    ${ElevationLevel(1)};
  }

  &::after {
    ${zIndex(ZIndexLevel.Low)};
  }
`

const LiveChatUnavailableMessageText = styled.div`
  ${ElevationLevel(1)};
  ${zIndex(ZIndexLevel.Low)};
  padding: ${spacing(2)};
  background-color: ${(props) => props.theme.colors.palette.greys.white};
  position: relative;
  border-radius: 4px;
`

export interface LiveChatContentProps {
  alternateText?: boolean
  outline?: boolean
}

export const LiveChatContent = (props: LiveChatContentProps) => {
  const [isActive, setIsActive] = React.useState<boolean>(false)
  const [showMessage, setShowMessage] = React.useState<boolean>(false)

  React.useEffect(() => {
    const interval = setInterval(() => {
      setIsActive(isLiveChatAvailable(document))
      isLiveChatAvailable(document) && setShowMessage(false)
    }, liveChatAvailabilityCheckInterval)
    return () => {
      clearInterval(interval)
    }
  }, [])

  const {
    icons: { chatOutline, chatFilled },
  } = useTheme()

  const triggerLivePerson = () => {
    if (!isActive) {
      setShowMessage(!showMessage)

      return
    }
    setShowMessage(false)

    isActive &&
      getLiveChatContainer(document) &&
      getLiveChatContainer(document)?.click()
  }

  const i18nText = {
    liveChat: i18n('myaccount.livechat.text'),
    liveChatUnavailable: i18n('account.livechat.unavailable'),
  }

  return (
    <LiveChatContainer
      type="button"
      onClick={triggerLivePerson}
      onBlur={() => setShowMessage(false)}
      data-testid="live-chat"
      alternateText={props.alternateText}
    >
      <LeftContent>
        {chatOutline?.svgPath && chatFilled?.svgPath ? (
          props.outline ? (
            <SvgIcon
              xmlns="http://www.w3.org/2000/svg"
              viewBox={chatOutline.viewBox}
              width={chatOutline.width}
              height={chatOutline.height}
            >
              <path d={chatOutline.svgPath} fillRule="evenodd" />
            </SvgIcon>
          ) : (
            <SvgIcon
              xmlns="http://www.w3.org/2000/svg"
              viewBox={chatFilled.viewBox}
              width={chatFilled.width}
              height={chatFilled.height}
            >
              <path d={chatFilled.svgPath} fillRule="evenodd" />
            </SvgIcon>
          )
        ) : props.outline ? (
          <LiveChatOutline />
        ) : (
          <LiveChat />
        )}
      </LeftContent>
      <Content>
        <span>{i18nText.liveChat}</span>
        <LiveChatIndicator
          isActive={isActive}
          data-testid={`live-chat-${isActive ? 'active' : 'inactive'}`}
        ></LiveChatIndicator>
        {showMessage && !isActive && (
          <LiveChatUnavailableMessage>
            <LiveChatUnavailableMessageText>
              {i18nText.liveChatUnavailable}
            </LiveChatUnavailableMessageText>
          </LiveChatUnavailableMessage>
        )}
      </Content>
    </LiveChatContainer>
  )
}
